/**
 * Join invites + join codes (WU-02, RolesUsersPlan §4.4, §8.5). Mirrors
 * `invites` (0030). A company-scope invite is the Owner's account number
 * (Owner/Admin only); project-scope invites optionally land the joiner on a
 * crew. Codes are 8 digits, displayed `1234-5678`. Not the scope-package
 * sub invites in seam.ts — hence `CreateJoinInviteRequest*`.
 */
import { z } from 'zod';
import { LoginClientSchema, LoginResponseSchema, PasswordSchema } from './auth';
import { CompanyUserSchema } from './company';
import { zEmail, zIsoDateTime, zUuid } from './common';
export const InviteScopeSchema = z.enum(['company', 'project']);
export const InviteRoleDefaultSchema = z.enum(['worker', 'foreman']);
/** Derived on read: revoked_at → revoked; expires_at past → expired; uses ≥ max_uses → exhausted. */
export const InviteStatusSchema = z.enum(['active', 'expired', 'revoked', 'exhausted']);
export const InviteSchema = z.object({
    id: zUuid,
    scope: InviteScopeSchema,
    projectId: zUuid.nullable(),
    projectName: z.string().nullable(),
    crewId: zUuid.nullable(),
    crewName: z.string().nullable(),
    roleDefault: InviteRoleDefaultSchema,
    /** Display form of the decrypted code. */
    code: z.string().regex(/^\d{4}-\d{4}$/, 'expected 1234-5678'),
    link: z.string().url(),
    expiresAt: zIsoDateTime,
    maxUses: z.number().int().min(1).nullable(),
    uses: z.number().int().min(0),
    status: InviteStatusSchema,
    createdAt: zIsoDateTime,
    createdBy: z
        .object({
        id: zUuid,
        name: z.string().max(200).nullable(),
    })
        .nullable(),
});
/** GET /invites */
export const InvitesListQuerySchema = z.object({
    scope: InviteScopeSchema.optional(),
    projectId: zUuid.optional(),
});
export const InvitesListResponseSchema = z.object({
    invites: z.array(InviteSchema),
});
/** POST /invites */
export const CreateJoinInviteRequestSchema = z
    .object({
    scope: InviteScopeSchema,
    projectId: zUuid.optional(),
    crewId: zUuid.optional(),
    roleDefault: InviteRoleDefaultSchema.default('worker'),
    expiresInDays: z.number().int().min(1).max(90).default(30),
    maxUses: z.number().int().min(1).max(1000).optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.scope === 'company') {
        if (v.projectId !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['projectId'],
                message: 'a company invite cannot name a project',
            });
        }
        if (v.crewId !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['crewId'],
                message: 'a company invite cannot name a crew',
            });
        }
        return;
    }
    if (v.projectId === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['projectId'],
            message: 'a project invite requires projectId',
        });
    }
});
/** POST /invites/:id/send */
export const SendInviteRequestSchema = z
    .object({
    email: zEmail,
})
    .strict();
/**
 * What actually happened to the message, so the UI never claims a delivery that
 * did not occur.
 *
 * - `queued`   — outbound mail is configured; the message is recorded and the
 *                drain will hand it to the provider. Still not a guarantee it
 *                lands in an inbox.
 * - `captured` — mail delivery is switched off on this deployment. The message
 *                exists only in the outbox and will never be sent; share the
 *                code or link instead.
 */
export const InviteSendResultSchema = z
    .object({
    ok: z.literal(true),
    delivery: z.enum(['queued', 'captured']),
})
    .strict();
// ------------------------------------------------------------------- joining
/** GET /public/join/:token and POST /public/join/code — what the joiner is about to join. */
export const JoinPreviewSchema = z.object({
    companyName: z.string(),
    projectName: z.string().nullable(),
    crewName: z.string().nullable(),
    roleDefault: InviteRoleDefaultSchema,
    expiresAt: zIsoDateTime,
    /** False when revoked, expired or exhausted. */
    usable: z.boolean(),
});
/** Accepts `12345678`, `1234-5678` or `1234 5678`; parses to the 8 bare digits. */
const zJoinCode = z.preprocess((v) => (typeof v === 'string' ? v.trim().replace(/^(\d{4})[- ]?(\d{4})$/, '$1$2') : v), z.string().regex(/^\d{8}$/, 'expected an 8-digit code'));
/** POST /public/join/code (preview) and POST /join (signed in). */
export const JoinCodeRequestSchema = z
    .object({
    code: zJoinCode,
})
    .strict();
/**
 * POST /public/join/:token/claim — creates a user only (never a company).
 * `signIn: true` (the mobile app) continues into a login for `client`, with the
 * normal surface and two-factor rules; the web join page omits it and ends on
 * "Open the app", so no session is minted for a browser that will not use it.
 */
export const JoinClaimRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    email: zEmail,
    password: PasswordSchema,
    signIn: z.boolean().default(false),
    client: LoginClientSchema.default('mobile'),
})
    .strict();
/** POST /public/join/code/claim */
export const JoinCodeClaimRequestSchema = JoinClaimRequestSchema.extend({
    code: zJoinCode,
}).strict();
/**
 * POST /public/join/:token/claim/google — the same claim with a Google identity
 * instead of an email + password. `signupToken` is the one /auth/google returned
 * as `signup_required`; the email comes from it, never from this body.
 */
export const GoogleJoinClaimRequestSchema = z
    .object({
    signupToken: z.string().min(20).max(4096),
    /** Overrides the name Google supplied; required when Google supplied none. */
    name: z.string().trim().min(1).max(120).optional(),
    signIn: z.boolean().default(false),
    client: LoginClientSchema.default('mobile'),
})
    .strict();
/** POST /public/join/code/claim/google */
export const GoogleJoinCodeClaimRequestSchema = GoogleJoinClaimRequestSchema.extend({
    code: zJoinCode,
}).strict();
/** POST /join (signed in, same company only) — membership added. */
export const JoinExistingResponseSchema = z.object({
    companyId: zUuid,
    projectId: zUuid.nullable(),
    crewId: zUuid.nullable(),
});
/** Claim success: the new user, where they joined, and the login continuation when `signIn` was asked. */
export const JoinClaimResponseSchema = z.object({
    user: CompanyUserSchema,
    companyName: z.string(),
    projectName: z.string().nullable(),
    crewName: z.string().nullable(),
    login: LoginResponseSchema.nullable(),
});
//# sourceMappingURL=invites.js.map